export const edgeId = (source, target) => `${source}->${target}`;

export function tierError(prod, comp) {
  if (!prod || !comp) return "Unknown item";
  if (prod.tier === 1) return `${prod.name} is Tier 1 and cannot have components`;
  if (prod.id === comp.id) return `${prod.name} cannot use itself`;
  if (comp.tier > prod.tier || (comp.tier === prod.tier && prod.tier !== 2)) return `${comp.name} (T${comp.tier}) cannot feed ${prod.name} (T${prod.tier})`;
  return null;
}

// adj: product -> components. Returns looping chain [a, b, ..., a] (a uses b) or null.
export function findCycle(edges) {
  const adj = {};
  edges.forEach((e) => { (adj[e.target] = adj[e.target] || []).push(e.source); });
  const color = {};
  const stack = [];
  const dfs = (n) => {
    color[n] = 1; stack.push(n);
    for (const m of adj[n] || []) {
      if (color[m] === 1) return [...stack.slice(stack.indexOf(m)), m];
      if (!color[m]) { const f = dfs(m); if (f) return f; }
    }
    stack.pop(); color[n] = 2; return null;
  };
  for (const n of Object.keys(adj)) if (!color[n]) { const f = dfs(n); if (f) return f; }
  return null;
}

export const cycleEdgeIds = (chain) => new Set((chain || []).slice(0, -1).map((p, i) => edgeId(chain[i + 1], p)));

export function stagesOf(nodes, edges) {
  const tier = Object.fromEntries(nodes.map((n) => [n.id, n.tier]));
  const kids = {};
  edges.forEach((e) => { if (tier[e.source] === 2) (kids[e.target] = kids[e.target] || []).push(e.source); });
  const memo = {};
  const st = (i, depth = 0) => {
    if (memo[i]) return memo[i];
    if (depth > 30) return 1;
    memo[i] = 1 + Math.max(0, ...(kids[i] || []).map((k) => st(k, depth + 1)));
    return memo[i];
  };
  return Object.fromEntries(nodes.filter((n) => n.tier === 2).map((n) => [n.id, st(n.id)]));
}

const COL_W = 400, NODE_W = 240, NODE_SLOT = 170, LANE_SLOT = 60;

// Each part sits one column before its nearest consumer; edges that still skip columns get reserved lanes.
function columnsOf(nodes, edges, stages) {
  const ids = new Set(nodes.map((n) => n.id));
  const outs = {};
  edges.forEach((e) => { if (ids.has(e.source) && ids.has(e.target)) (outs[e.source] = outs[e.source] || []).push(e.target); });
  const maxStage = Math.max(0, ...Object.values(stages));
  const col = {};
  const rank = (n) => (n.tier === 3 ? 0 : n.tier === 2 ? 1 + maxStage - stages[n.id] : 99);
  [...nodes].sort((a, b) => rank(a) - rank(b)).forEach((n) => {
    if (n.tier === 3) { col[n.id] = maxStage + 1; return; }
    const cs = (outs[n.id] || []).map((t) => col[t]).filter((c) => c != null);
    col[n.id] = cs.length ? Math.min(...cs) - 1 : n.tier === 2 ? stages[n.id] : 0;
  });
  const min = Math.min(0, ...Object.values(col));
  Object.keys(col).forEach((k) => { col[k] -= min; });
  return col;
}

export function autoLayout(nodes, edges) {
  const stages = stagesOf(nodes, edges);
  const col = columnsOf(nodes, edges, stages);
  const name = Object.fromEntries(nodes.map((n) => [n.id, n.name]));
  const slots = {}, next = {};
  const add = (key, c) => { (slots[c] = slots[c] || []).push(key); };
  nodes.forEach((n) => add(n.id, col[n.id]));
  const lanes = {};
  edges.forEach((e) => {
    if (col[e.source] == null || col[e.target] == null) return;
    let prev = e.source;
    for (let c = col[e.source] + 1; c < col[e.target]; c++) {
      const k = `lane:${e.id}:${c}`; add(k, c); (lanes[e.id] = lanes[e.id] || []).push(k);
      (next[prev] = next[prev] || []).push(k); prev = k;
    }
    (next[prev] = next[prev] || []).push(e.target);
  });
  const row = {};
  const cols = Object.keys(slots).map(Number).sort((a, b) => b - a);
  cols.forEach((c, ci) => {
    const bary = (k) => { const r = (next[k] || []).map((t) => row[t]).filter((v) => v != null); return r.length ? r.reduce((a, b) => a + b, 0) / r.length : 1e6; };
    const order = ci === 0 ? [...slots[c]].sort((a, b) => (name[a] || a).localeCompare(name[b] || b))
      : [...slots[c]].sort((a, b) => bary(a) - bary(b) || (name[a] || a).localeCompare(name[b] || b));
    order.forEach((k, i) => { row[k] = i; });
    slots[c] = order;
  });
  const pos = {}, lanePt = {};
  Object.entries(slots).forEach(([c, keys]) => {
    let y = 0;
    keys.forEach((k) => {
      if (k.startsWith("lane:")) { lanePt[k] = { x: Number(c) * COL_W, y: y + LANE_SLOT / 2 }; y += LANE_SLOT; }
      else { pos[k] = { x: Number(c) * COL_W, y }; y += NODE_SLOT; }
    });
  });
  const routes = Object.fromEntries(Object.entries(lanes).map(([id, ks]) => [id, ks.map((k) => lanePt[k])]));
  return { pos, stages, routes, nodeWidth: NODE_W };
}

// Total quantity of every upstream part per 1 unit of root.
export function upstreamTotals(root, edges) {
  const into = {};
  edges.forEach((e) => { (into[e.target] = into[e.target] || []).push(e); });
  const totals = { [root]: 1 };
  const walk = (n, mult, depth) => {
    if (depth > 30) return;
    (into[n] || []).forEach((e) => { totals[e.source] = (totals[e.source] || 0) + e.qty * mult; walk(e.source, e.qty * mult, depth + 1); });
  };
  walk(root, 1, 0);
  return totals;
}
